// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { documentsApi } from '@shared/data-access/api/engine/documents-api';
import { illiquidsApi } from '@shared/data-access/api/engine/illiquids-api';
import type { TagEntity } from '@shared/data-access/interfaces/redux/tags-management-state-types';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import {
  IlliquidAssetType,
  type IlliquidAsset,
} from '@shared/data-access/types/engine-types';
import { useConfirmPopup } from '@shared/ui-kit/components';
import { useDrawer } from '@shared/ui-kit/hooks/use-drawer';
import { useSnackbar } from '@shared/ui-kit/providers/snackbar-provider';
import { isDefined, isNullOrUndefined } from '@shared/utils';
import { useIlliquidAssetsClientId } from '../../hooks/use-illiquid-assets-client-id';

export interface UseIlliquidAssetDetailDrawerProps {
  readonly open: boolean;

  readonly onClose: () => void;

  readonly asset?: IlliquidAsset;

  readonly tags?: readonly TagEntity[];

  readonly initialAssignedTags?: readonly TagEntity[];

  readonly onChangeTagIds?: (ids: number[]) => void;

  readonly onCreateTag?: (name: string) => void;

  readonly onDelete: () => void;
}

const areIbansEqual = (a: readonly string[], b: readonly string[]): boolean => {
  if (a.length !== b.length) return false;

  const sortedA = [...a].sort();

  const sortedB = [...b].sort();

  return sortedA.every((iban, index) => iban === sortedB[index]);
};

export const useIlliquidAssetDetailDrawer = ({
  open,
  onClose,
  asset,
  tags,
  initialAssignedTags,
  onChangeTagIds,
  onCreateTag,
  onDelete,
}: UseIlliquidAssetDetailDrawerProps) => {
  const { t } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();

  const showConfirm = useConfirmPopup();

  const clientId = useIlliquidAssetsClientId();

  const accountCurrency = useSelector(selectAccountCurrency);

  documentsApi.useListDocumentsQuery(undefined, { skip: !open });

  const [updateIlliquidAsset] = illiquidsApi.useUpdateIlliquidAssetMutation();

  const [draftIbans, setDraftIbans] = useState<string[]>([]);

  const [isSaving, setIsSaving] = useState(false);

  const assetId = asset?.id;

  const serverIbans = useMemo(() => asset?.ibans ?? [], [asset?.ibans]);

  const isDirty = useMemo(
    () => !areIbansEqual(draftIbans, serverIbans),
    [draftIbans, serverIbans],
  );

  const onLinkIban = useCallback((iban: string) => {
    setDraftIbans(prev => (prev.includes(iban) ? prev : [iban, ...prev]));
  }, []);

  const onUnlinkIban = useCallback((iban: string) => {
    setDraftIbans(prev => prev.filter(current => current !== iban));
  }, []);

  const saveLinkedAccounts = useCallback(async () => {
    if (!isDefined(asset) || !isDirty) return;

    setIsSaving(true);

    try {
      await updateIlliquidAsset({
        asset_id: asset.id,
        client_id: clientId,
        currency: accountCurrency,
        body: { ibans: draftIbans },
      }).unwrap();

      enqueueSnackbar(
        t('illiquid-asset-drawer.messages.linked-accounts-saved', {
          ns: 'components',
        }),
        { variant: 'success' },
      );
    } catch {
      enqueueSnackbar(
        t('illiquid-asset-drawer.messages.linked-accounts-save-error', {
          ns: 'components',
        }),
        { variant: 'error' },
      );

      throw new Error('linked-accounts-save-failed');
    } finally {
      setIsSaving(false);
    }
  }, [
    asset,
    isDirty,
    updateIlliquidAsset,
    clientId,
    accountCurrency,
    draftIbans,
    enqueueSnackbar,
    t,
  ]);

  const onSaveLinkedAccounts = useCallback(() => {
    void saveLinkedAccounts().catch(() => undefined);
  }, [saveLinkedAccounts]);

  const requestClose = useCallback(() => {
    if (!isDirty) {
      onClose();

      return;
    }

    showConfirm({
      title: t('illiquid-asset-drawer.unsaved-linked-accounts-title', {
        ns: 'components',
      }),
      message: t('illiquid-asset-drawer.unsaved-linked-accounts-message', {
        ns: 'components',
      }),
      confirmText: t('common.save-changes', { ns: 'common' }),
      cancelText: t('illiquid-asset-drawer.unsaved-linked-accounts-discard', {
        ns: 'components',
      }),
      onConfirm: async () => {
        try {
          await saveLinkedAccounts();

          onClose();
        } catch {
          // Keep drawer open; snackbar already shown.
        }
      },
      onCancel: async () => {
        onClose();
      },
    });
  }, [isDirty, onClose, saveLinkedAccounts, showConfirm, t]);

  const [DrawerRenderer, drawerControls] = useDrawer({
    open,
    onClose: requestClose,
  });

  const isAcquisitionAsset = useMemo(
    () =>
      isDefined(asset) &&
      asset.category !== null &&
      asset.category !== IlliquidAssetType.PeVc,
    [asset],
  );

  const acquisitionPerformanceRatio = useMemo((): number | null => {
    if (asset?.category === IlliquidAssetType.PeVc) {
      return null;
    }

    const acquisitionValue = asset?.local_currency_values?.acquisition_value;

    const current = asset?.local_currency_values?.current_valuation;

    if (
      isNullOrUndefined(acquisitionValue) ||
      acquisitionValue <= 0 ||
      isNullOrUndefined(current)
    ) {
      return null;
    }

    return (current - acquisitionValue) / acquisitionValue;
  }, [asset]);

  const tagManagement =
    isDefined(tags) &&
    isDefined(initialAssignedTags) &&
    isDefined(onChangeTagIds) &&
    isDefined(onCreateTag)
      ? { tags, initialAssignedTags, onChangeTagIds, onCreateTag }
      : null;

  useEffect(() => {
    if (!open || !isDefined(asset)) return;

    setDraftIbans(asset.ibans ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only on open / asset identity
  }, [open, assetId]);

  return {
    DrawerRenderer,
    open: drawerControls.open,
    onOpen: drawerControls.onOpen,
    onClose: requestClose,
    t,
    asset,
    isAcquisitionAsset,
    acquisitionPerformanceRatio,
    tagManagement,
    draftIbans,
    onLinkIban,
    onUnlinkIban,
    isDirty,
    isSaving,
    onSaveLinkedAccounts,
  } as const;
};
